import { Card, message } from 'antd';
import React, { useEffect, useRef, useState } from 'react';
import { BlobIndexTag, BlobMetadataField } from '../../../../../../generated';
import { FilePicker } from './file-picker';
import { FilePickerCoverLetterFileSelection as CoverLetterFileSelection } from './file-picker-cover-letter-file-selection';
import { AuthResult, AzureBlobResponse } from '../../../../../shared/file-upload/azure-upload';
import { FileValidator } from '../../../../../shared/file-upload/file-validator';
import axios, { AxiosProgressEvent } from 'axios';
import { RcFile } from 'antd/es/upload';

interface CoverLetterDocumentPickerProps {
  onCancel: () => void;
  generateCoverLetterPdf: (data: any) => Promise<File>;
  generateCoverLetterPdfData: (otherData: string[], photoUrl: string) => any;
  authorizeRequest: (file: File) => Promise<AuthResult>;
  onSuccess: (response: AzureBlobResponse) => Promise<void>;
  isProcessingUploadingFile?: boolean;
  isProcessing: boolean;
}

export interface SelectedFileInfo {
  name: string;
  url: string;
  type: string;
  caseType: string;
  caseId: string;
}

export interface SelectedFilesMap {
  [key: string]: SelectedFileInfo | null;
}

export const CoverLetterDocumentPicker: React.FC<CoverLetterDocumentPickerProps> = (props) => {
  const [selectedChecklistFiles, setSelectedChecklistFiles] = useState<string[]>([]);
  const authResultRef = useRef<AuthResult | undefined>(undefined);
  const initialFiles = {
    photo: {
      name: '',
      url: '',
      type: '',
      caseType: '',
      caseId: ''
    }
  };

  function useFileSelection(initialFiles: SelectedFilesMap) {
    const [selectedFilesMap, setSelectedFilesMap] = useState<SelectedFilesMap>(initialFiles);
    const [currentFileSelection, setCurrentFileSelection] = useState<string>('');

    const handleFileSelection = (fileInfo: SelectedFileInfo) => {
      setSelectedFilesMap((prevFilesMap) => ({
        ...prevFilesMap,
        [currentFileSelectionRef.current]: fileInfo
      }));
    };

    const handleCancel = () => {
      props.onCancel();
      setCurrentFileSelection('');
      setSelectedFilesMap(initialFiles);
    };

    return {
      selectedFilesMap,
      currentFileSelection,
      setCurrentFileSelection,
      handleFileSelection,
      onCancel: handleCancel
    };
  }

  const { selectedFilesMap, currentFileSelection, setCurrentFileSelection, handleFileSelection, onCancel: handleCancel } = useFileSelection(initialFiles);

  const handleSelectedFilesChange = (selected: string[]) => {
    setSelectedChecklistFiles(selected); // Store selected files in state
  };

  function renameFile(originalFile: File, newName: string): File {
    try {
      return new File([originalFile], newName, {
        type: originalFile.type,
        lastModified: originalFile.lastModified
      });
    } catch (error) {
      console.error('Error occurred while renaming file:', error);
      return originalFile;
    }
  }

  const validateFile = async (file: File) => {
    const sanitizedFilename = file.name.replace(/\s+/g, '-');
    const sanitizedFile = renameFile(file, sanitizedFilename) as RcFile;

    const validatorOptions = {
      maxFileSizeBytes: 10000000 // 10 MB
    };

    const validator = new FileValidator(sanitizedFile, validatorOptions);
    const result = await validator.validate();

    if (!result.success) {
      console.error(`Error: ${result.message}`);

      switch (result.code) {
        case 'content-type':
          console.log('issue with content-type');
          break;
        case 'content-length':
          console.log('issue with content-length');
          break;
        case 'default':
          message.error(`File did not upload, error: ${JSON.stringify(result.message)}`);
          break;
      }

      return null;
    }

    return result.file;
  };

  const getMetadataHeaders = (metadataFields?: BlobMetadataField[]) => {
    let headers: Record<string, string> = {};
    if (metadataFields) {
      for (const field of metadataFields) {
        headers[`x-ms-meta-${field.name}`] = field.value;
      }
    }
    return headers;
  };

  const getIndexTagsHeaders = (indexTags?: BlobIndexTag[]) => {
    let headers: Record<string, string> = {};
    if (indexTags) {
      const tags = indexTags.reduce((acc: Record<string, string>, tag) => {
        acc[tag.name] = tag.value;
        return acc;
      }, {});
      headers['x-ms-tags'] = new URLSearchParams(tags).toString();
    }
    return headers;
  };

  const uploadFile = async (file: File) => {
    if (authResultRef.current?.isAuthorized) {
      const { authHeader } = authResultRef.current;

      try {
        const metadataHeaders = getMetadataHeaders(authHeader.metadataFields);
        const indexTagsHeaders = getIndexTagsHeaders(authHeader.indexTags);

        const headers = {
          ...metadataHeaders,
          ...indexTagsHeaders,
          Authorization: authHeader.authHeader,
          'x-ms-blob-type': 'BlockBlob',
          'x-ms-version': '2021-04-10',
          'x-ms-date': authHeader.requestDate,
          'Content-Type': file.type
        };

        const response = await axios.put(authHeader.blobPath ?? '', file, {
          headers,
          onUploadProgress: (progressEvent: AxiosProgressEvent) => {
            if (progressEvent.total) {
              const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
              console.log(`Upload Progress: ${percentCompleted}%`);
            }
          }
        });

        const blobResponse: AzureBlobResponse = {
          blobUrl: response.request.responseURL,
          versionId: response.headers['x-ms-version-id']
        };

        console.log('uploaded:', file);
        console.log('blobResponse:', blobResponse);

        await props.onSuccess(blobResponse);
      } catch (uploadError) {
        console.error('Error uploading asset: ', uploadError);
        message.error('Failed to upload file');
      }
    }
  };

  const handleGeneratePdf = async () => {
    try {
      const requiredFiles = ['photo'];
      const allFilesPresent = requiredFiles.every((file) => selectedFilesMap[file]?.name?.trim());
      if (!allFilesPresent) {
        message.error('Photo is required');
        return
      }
      const fileInfo = selectedFilesMap['photo'];
      const { combinedData } = await props.generateCoverLetterPdfData(selectedChecklistFiles, fileInfo?.url ?? '');
      const generatedPdf = await props.generateCoverLetterPdf(combinedData);
      const validFile = await validateFile(generatedPdf);

      if (validFile) {
        const authResult = await props.authorizeRequest(validFile);
        if (authResult.isAuthorized) {
          authResultRef.current = authResult;
          await uploadFile(validFile);
        }
      }
    } catch (error) {
      console.error(error);
    }
  };


  const files = ['Verification Report', 'Verified Credential', 'Credential Translation', 'Verification Form'];  

  const imageFile = {
    type: 'photo',
    label: 'Select',
    fileName: 'photo.jpg',
    isRequired: true
  };

  const currentFileSelectionRef = useRef(currentFileSelection);
  useEffect(() => {
    currentFileSelectionRef.current = currentFileSelection;
  }, [currentFileSelection]);

  const currentFilePicker = `File Picker - ${currentFileSelection}`;
  return (
    <div
      style={{
        display: 'flex'
      }}
    >
      <CoverLetterFileSelection
        title="Generate Cover Letter"
        files={files}
        selectedFilesMap={selectedFilesMap}
        onFileSelect={setCurrentFileSelection}
        currentFileSelection={currentFileSelection}
        onCancel={handleCancel}
        onGenerateDocument={handleGeneratePdf}
        imageFile={imageFile}
        onSelectedFilesChange={handleSelectedFilesChange}
        isLoading={props.isProcessing}
        isProcessingUploadingFile={props.isProcessingUploadingFile}
      />
      {currentFileSelection !== '' && (
        <Card title={currentFilePicker}>
          <FilePicker handleFileSelection={handleFileSelection} isCoverLetter={true} />
        </Card>
      )}
    </div>
  );
};
